import {
  AuiIf,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
  useAuiState,
  type DataMessagePartProps,
} from '@assistant-ui/react'
import { useEffect, useRef } from 'react'
import ChartRenderer from '@/components/ChartRenderer'
import DisclaimerCaption from '@/components/DisclaimerCaption'
import FlagButton from '@/components/FlagButton'
import ViewQueryPanel from '@/components/ViewQueryPanel'
import {
  getActiveRemoteThreadId,
  setActiveRemoteThreadId,
} from '@/runtime/activeThreadId'
import type { InquisitorResponseData } from '@/types/chat'

const EXAMPLE_QUESTIONS = [
  'How much did we sell company-wide over the last 7 days by day?',
  'What were our top-selling items last 30 days?',
  "What's driving the change in revenue recently?",
  'Which items are frequently bought together?',
] as const

const TITLE_MAX_LEN = 60

function extractTextFromMessageContent(content: unknown): string {
  if (typeof content === 'string') {
    return content.trim()
  }
  if (!Array.isArray(content)) {
    return ''
  }
  return content
    .map((part) => {
      if (!part || typeof part !== 'object') return ''
      const record = part as { type?: unknown; text?: unknown }
      if (record.type === 'text' && typeof record.text === 'string') {
        return record.text
      }
      return ''
    })
    .join('\n')
    .trim()
}

function truncateTitle(text: string): string {
  const cleaned = text.trim()
  if (cleaned.length <= TITLE_MAX_LEN) return cleaned
  return cleaned.slice(0, TITLE_MAX_LEN)
}

function firstUserMessageText(
  messages: ReadonlyArray<{ role: string; content: unknown }>,
): string | undefined {
  for (const message of messages) {
    if (message.role !== 'user') continue
    const text = extractTextFromMessageContent(message.content)
    if (text) return truncateTitle(text)
  }
  return undefined
}

function InquisitorDataPart({ data }: DataMessagePartProps<InquisitorResponseData>) {
  return <ChartRenderer data={data} />
}

function AssistantMessageFooter() {
  const { logId, debug } = useAuiState((state) => {
    const custom = state.message.metadata?.custom as unknown as
      | InquisitorResponseData
      | undefined
    if (!custom || typeof custom.log_id !== 'string' || !custom.log_id) {
      return { logId: undefined, debug: custom?.debug ?? undefined }
    }
    return {
      logId: custom.log_id,
      debug: custom.debug ?? undefined,
    }
  })

  return (
    <AuiIf condition={(state) => state.message.role === 'assistant'}>
      <div className="mt-2 flex flex-wrap items-start gap-x-4 gap-y-1">
        <ViewQueryPanel debug={debug} />
        <FlagButton logId={logId} />
      </div>
    </AuiIf>
  )
}

/** Create the remote thread early so sample/send isn't blocked on POST /threads.
 * Empty-titled drafts stay hidden from the sidebar list. */
function PreinitializeEmptyThread() {
  const aui = useAui()
  const isEmpty = useAuiState((state) => state.thread.isEmpty)
  const remoteId = useAuiState((state) => state.threadListItem.remoteId)

  useEffect(() => {
    if (!isEmpty || remoteId) return
    void aui
      .threadListItem()
      .initialize()
      .then(({ remoteId: id }) => {
        setActiveRemoteThreadId(id)
      })
      .catch((error: unknown) => {
        console.warn('[inquisitor] thread pre-initialize failed', error)
      })
  }, [aui, isEmpty, remoteId])

  return null
}

/**
 * Preinit moves the thread out of assistant-ui "new" status, so the built-in
 * runEnd → generateTitle hook never runs. Rename from the first user message
 * as soon as it exists (in parallel with the LLM run).
 */
function GenerateTitleAfterFirstMessage() {
  const aui = useAui()
  const remoteId = useAuiState((state) => state.threadListItem.remoteId)
  const title = useAuiState((state) => state.threadListItem.title)
  const isEmpty = useAuiState((state) => state.thread.isEmpty)
  const messages = useAuiState((state) => state.thread.messages)
  const namedForRemoteIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    namedForRemoteIdRef.current = undefined
  }, [remoteId])

  useEffect(() => {
    if (!remoteId || isEmpty) return
    if (namedForRemoteIdRef.current === remoteId) return

    const displayTitle = (title ?? '').trim()
    const needsTitle = !displayTitle || displayTitle === 'New chat'
    if (!needsTitle) {
      namedForRemoteIdRef.current = remoteId
      return
    }

    const newTitle = firstUserMessageText(messages)
    if (!newTitle) return

    namedForRemoteIdRef.current = remoteId
    void (async () => {
      try {
        await aui.threadListItem().rename(newTitle)
      } catch (error: unknown) {
        namedForRemoteIdRef.current = undefined
        console.warn('[inquisitor] thread title rename failed', error)
      }
    })()
  }, [aui, remoteId, title, isEmpty, messages])

  return null
}

/** Keep the imperative remoteId cache in sync with React state. */
function SyncActiveRemoteThreadId() {
  const remoteId = useAuiState((state) => state.threadListItem.remoteId)

  useEffect(() => {
    setActiveRemoteThreadId(remoteId)
  }, [remoteId])

  return null
}

function RunningOverlay() {
  const isRunning = useAuiState((state) => state.thread.isRunning)
  if (!isRunning) return null
  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-2 text-sm text-gray-500">
      Thinking…
    </div>
  )
}

function ExampleQuestions() {
  const aui = useAui()
  const isRunning = useAuiState((state) => state.thread.isRunning)

  async function askExample(question: string) {
    if (isRunning) return
    try {
      const threadState = aui.thread().getState()
      if (threadState.messages.length > 0) {
        await aui.threads().switchToNewThread()
      }
      // Never await initialize before painting the user message — that was the
      // multi-second lag. Prefer preinit / imperative cache; chatRuntime will
      // create a thread as a last resort if both are missing.
      const remoteId =
        aui.threadListItem().getState().remoteId ?? getActiveRemoteThreadId()
      if (remoteId) {
        setActiveRemoteThreadId(remoteId)
      }
      aui.thread().append({
        role: 'user',
        content: [{ type: 'text', text: question }],
        startRun: true,
      })
    } catch (error) {
      console.error('[inquisitor] failed to start example question', error)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-4 px-4 py-12">
      <div className="text-center">
        <h2 className="text-lg font-medium text-gray-900">Ask about the business</h2>
        <p className="mt-1 text-sm text-gray-500">
          Start with a question or pick an example below.
        </p>
      </div>
      <div className="grid w-full gap-2 sm:grid-cols-2">
        {EXAMPLE_QUESTIONS.map((question) => (
          <button
            key={question}
            type="button"
            disabled={isRunning}
            onClick={() => void askExample(question)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 transition hover:border-[var(--color-info-600)] hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  )
}

function ChatComposer() {
  const isRunning = useAuiState((state) => state.thread.isRunning)

  return (
    <div className="border-t border-gray-200 bg-white px-4 py-3">
      <div className="mx-auto w-full max-w-3xl space-y-2">
        <ComposerPrimitive.Root className="flex items-end gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
          <ComposerPrimitive.Input
            placeholder={
              isRunning
                ? 'Waiting for a response…'
                : 'Ask a question about sales, items, or revenue…'
            }
            disabled={isRunning}
            className="max-h-40 min-h-[2.75rem] flex-1 resize-none bg-transparent py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
            rows={1}
          />
          {isRunning ? (
            <button
              type="button"
              disabled
              className="btn btn-primary min-h-11 min-w-[5.5rem] shrink-0 px-4 opacity-50"
            >
              …
            </button>
          ) : (
            <ComposerPrimitive.Send className="btn btn-primary min-h-11 min-w-[5.5rem] shrink-0 px-4">
              Send
            </ComposerPrimitive.Send>
          )}
        </ComposerPrimitive.Root>
        <DisclaimerCaption />
      </div>
    </div>
  )
}

export default function ChatThread() {
  return (
    <ThreadPrimitive.Root className="flex h-full min-h-0 flex-1 flex-col bg-gray-50">
      <SyncActiveRemoteThreadId />
      <PreinitializeEmptyThread />
      <GenerateTitleAfterFirstMessage />
      <ThreadPrimitive.Viewport className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* Bottom-align the message stack like ChatGPT */}
        <div className="mt-auto flex flex-col">
          <AuiIf condition={(state) => state.thread.isEmpty}>
            <ExampleQuestions />
          </AuiIf>

          <ThreadPrimitive.Messages
            components={{
              Message: () => (
                <MessagePrimitive.Root className="mx-auto flex w-full max-w-3xl px-4 py-3">
                  <div className="flex w-full flex-col">
                    <AuiIf condition={(state) => state.message.role === 'user'}>
                      <div className="ml-auto max-w-[85%] rounded-2xl bg-[#007AFF] px-4 py-2.5 text-sm text-white">
                        <MessagePrimitive.Parts />
                      </div>
                    </AuiIf>

                    <AuiIf condition={(state) => state.message.role === 'assistant'}>
                      <div className="max-w-full space-y-2">
                        <MessagePrimitive.Parts
                          components={{
                            Text: ({ text }) => (
                              <p className="sr-only">{text}</p>
                            ),
                            data: {
                              by_name: {
                                'inquisitor-response': InquisitorDataPart,
                              },
                            },
                          }}
                        />
                        <AssistantMessageFooter />
                      </div>
                    </AuiIf>
                  </div>
                </MessagePrimitive.Root>
              ),
            }}
          />
          <RunningOverlay />
        </div>
      </ThreadPrimitive.Viewport>

      <ChatComposer />
    </ThreadPrimitive.Root>
  )
}
